import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { toggleModalWithTimer, toggleSpinner } from 'apps/auth/actions/appAction';
import { changeFormField, changeFormFieldValue, setNewFields, updateFields, updateFormProperties } from 'apps/auth/actions/formAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import BaseLayout from 'apps/auth/components/common/Layout/BaseLayout';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import CvvPopup from 'apps/auth/components/common/popups/CvvPopup';
import Form from 'apps/auth/components/form-multi/Form';
import NewCardScreen from 'apps/auth/components/request/NewCardScreen';
import PayFromFields from 'apps/auth/components/request/PayFromFields';
import PaymentMethodScreen from 'apps/auth/components/request/PaymentMethodScreen';
import PaymentMethodScreenSorted from 'apps/auth/components/request/PaymentMethodScreenSorted';
import PayPrecheckPopup from 'apps/auth/components/request/PayPrecheckPopup';
import { REQUEST_ADDITIONAL_FIELDS, REQUEST_NEW_ACCOUNT, REQUEST_NEW_CARD, REQUEST_PAYMENT_METHOD } from 'apps/auth/components/request/RequestConstants';
import OnboardingQuestionnaireModal from 'apps/auth/components/send/modals/OnboardingQuestionnaireModal';
import Failed from 'apps/auth/components/sharedComponents/status/Failed';
import Success from 'apps/auth/components/sharedComponents/status/Success';
import Waiting from 'apps/auth/components/sharedComponents/status/Waiting';
import BasePage from 'apps/auth/components/utils/BasePage';
import ModalNew from 'apps/auth/components/utils/ModalNew';
import { PAYMENT_BANK_CARD, PAYMENT_BONUS, PAYMENT_GLOBAL_ACCOUNT, STATUS_OK } from 'apps/auth/constants/Constants';
import { AMOUNT_FROM, ID, PAYMENT_METHOD, SOURCE, SOURCE_CARD_CVV, SOURCE_CODE_FROM } from 'apps/auth/constants/fieldsName';
import { REQUEST_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_REQUEST_MAIN_LINK, AUTH_REQUEST_PAY_LINK, AUTH_REQUEST_PAY_PRECHECK } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import { resolveArguments } from 'apps/auth/helpers/utils';
import { useExtra, useFields, usePageMessages } from 'apps/auth/hooks/selectors';
import { checkFromField, GTRequestMultiScreenService } from 'apps/auth/services/GTRequestMultiScreenService';

const RequestPayPage = (props) => {
    const fields = useFields(REQUEST_FORM_NAME);
    const { requestContinueButtonTitle, cardAdded, enterCvvPopupTitle } = usePageMessages();
    const { sourcesFrom, sendFromBonusResource, sendFromGAResources, paymentMethodsOrder, availablePaymentMethodsList, useQuestionnaireScreen, questionnaire } =
        useExtra();
    const submitRef = useRef(null);
    const popupRef = useRef(null);
    const modalRef = useRef(null);

    const [screen, setScreen] = useState(REQUEST_PAYMENT_METHOD);
    const [disabledSubmitButton, setDisabledSubmitButton] = useState(true);
    const [paymentStatus, setPaymentStatus] = useState({
        isShow: false,
        status: {},
    });

    const actionUrl = `${AUTH_REQUEST_PAY_LINK}/${fields[ID]?.value}`;
    const actionKeepAmount = `${actionUrl}?keepAmount=true`;
    const hasPaymentMethodsList = Object.keys(availablePaymentMethodsList).length > 0;
    const hasPaymentMethodsOrder = paymentMethodsOrder?.sourceOptionsGroups?.length > 0;

    useEffect(() => {
        if (fields) {
            GTRequestMultiScreenService.toggleContinueButton(fields, screen, setDisabledSubmitButton);
        }
    }, [fields, screen]);

    const handleClose = () => {
        popupRef.current?.open(<PayPrecheckPopup handleClose={showPreCheck} submitRef={submitRef} popupRef={popupRef} formName={REQUEST_FORM_NAME} />);
    };

    const openPrecheckPopup = () => {
        popupRef.current?.open(<PayPrecheckPopup handleClose={handleClose} submitRef={submitRef} popupRef={popupRef} formName={REQUEST_FORM_NAME} />);
    };

    const showPreCheck = () => {
        if (useQuestionnaireScreen && questionnaire?.fields?.length > 0) {
            modalRef.current?.open(<OnboardingQuestionnaireModal modalRef={modalRef} onSubmitSuccess={openPrecheckPopup} />);
            return;
        }
        openPrecheckPopup();
    };

    const showCvvPopup = (card) => {
        popupRef.current?.open(
            <CvvPopup
                formName={REQUEST_FORM_NAME}
                title={resolveArguments(enterCvvPopupTitle, `${card?.alias} ·· ${card?.description}`)}
                submitRef={submitRef}
                popupRef={popupRef}
            />,
        );
    };
    const hasFromFields = (fieldsArr) => {
        if (fieldsArr) {
            return Object.values(fields).some((field) => checkFromField(field));
        }
    };

    const submitHandler = (_fieldsForSubmit, json) => {
        const newFields = json?.forms[REQUEST_FORM_NAME]?.fields;
        const hasNoErrors = Object.keys(json?.errors).length === 0;
        const isBankCard = _fieldsForSubmit?.[PAYMENT_METHOD]?.value === PAYMENT_BANK_CARD;

        if (screen === REQUEST_PAYMENT_METHOD && newFields) {
            props.setNewFields(REQUEST_FORM_NAME, newFields).then(() => {
                props.updateFormProperties(REQUEST_FORM_NAME, AUTH_REQUEST_PAY_PRECHECK, true).then(() => switchToNextScreen());
            });
        } else if (hasNoErrors && isBankCard && (screen === REQUEST_NEW_CARD || screen === REQUEST_ADDITIONAL_FIELDS)) {
            props.toggleModalWithTimer(true, cardAdded, () => {
                const showAddFields = hasFromFields(fields) && screen === REQUEST_NEW_CARD;
                props.toggleModalWithTimer(false, '', false).then(() => {
                    processJson(json);
                    switchToNextScreen(showAddFields);
                });
            });
        } else if (hasNoErrors) {
            processJson(json);
        }
    };

    const processJson = (json = {}) => {
        if (Object.keys(json.errors).length === 0) {
            if (json.extra?.precheck) {
                showPreCheck();
                if (screen === REQUEST_ADDITIONAL_FIELDS) {
                    switchToNextScreen();
                }
            } else if (json.extra?.showStatus && json.extra?.payment_status) {
                setPaymentStatus({
                    isShow: true,
                    status: json.extra.payment_status,
                });
            }
        }
    };

    const toggleProvider = (newFields, isSwitchToNextScreen = false, card = {}) => {
        props.toggleSpinner(true);
        apiFetchData(actionKeepAmount, newFields)
            .then((json) => {
                const jsonFields = json?.forms[REQUEST_FORM_NAME]?.fields;
                props.updateFields(REQUEST_FORM_NAME, jsonFields).then(() => {
                    props.updateFormProperties(REQUEST_FORM_NAME, AUTH_REQUEST_PAY_PRECHECK, false).then(() => {
                        if (Object.keys(card).length > 0) {
                            showCvvPopup(card);
                        } else if (isSwitchToNextScreen) {
                            switchToNextScreen(true);
                        } else {
                            submitRef.current.click();
                        }
                    });
                });
            })
            .catch((error) => {
                logWarn(error);
            })
            .finally(() => props.toggleSpinner(false));
    };

    const addNewCard = () => {
        props.changeFormFieldValue(REQUEST_FORM_NAME, PAYMENT_BANK_CARD, PAYMENT_METHOD).then(() => {
            props.updateFormProperties(REQUEST_FORM_NAME, actionUrl, false).then(() => submitRef.current.click());
        });
    };

    const selectedCard = (card, newFields, paymentMethod) => {
        props.updateFormProperties(REQUEST_FORM_NAME, AUTH_REQUEST_PAY_PRECHECK, false).then(() => {
            const isNotBankCard = paymentMethod !== PAYMENT_BANK_CARD;
            if (hasFromFields(newFields) && !isNotBankCard) {
                switchToNextScreen(true);
            } else if (hasFromFields(newFields) && isNotBankCard) {
                toggleProvider(newFields, true);
            } else if (isNotBankCard) {
                toggleProvider(newFields, false, card);
            } else {
                showCvvPopup(card);
            }
        });
    };

    const selectedGooglePay = (newFields) => {
        props.updateFormProperties(REQUEST_FORM_NAME, AUTH_REQUEST_PAY_PRECHECK, false).then(() => {
            if (hasFromFields(newFields)) {
                switchToNextScreen(true);
            } else {
                submitRef.current.click();
            }
        });
    };

    const selectedBonus = (newFields, paymentMethod) => {
        props.updateFormProperties(REQUEST_FORM_NAME, AUTH_REQUEST_PAY_PRECHECK, false).then(() => {
            const isNotBonus = paymentMethod !== PAYMENT_BONUS;
            if (hasFromFields(fields) && !isNotBonus) {
                switchToNextScreen(true);
            } else if (hasFromFields(fields) && isNotBonus) {
                toggleProvider(newFields, true);
            } else if (isNotBonus) {
                toggleProvider(newFields);
            } else {
                submitRef.current.click();
            }
        });
    };

    const selectedGAccount = (newFields, paymentMethod) => {
        props.updateFormProperties(REQUEST_FORM_NAME, AUTH_REQUEST_PAY_PRECHECK, false).then(() => {
            const isNotGa = paymentMethod !== PAYMENT_GLOBAL_ACCOUNT;
            if (hasFromFields(fields) && !isNotGa) {
                switchToNextScreen(true);
            } else if (hasFromFields(fields) && isNotGa) {
                toggleProvider(newFields, true);
            } else if (isNotGa) {
                toggleProvider(newFields);
            } else {
                submitRef.current.click();
            }
        });
    };

    const switchToNextScreen = (showAddFields = false) => {
        if (screen === REQUEST_PAYMENT_METHOD && !showAddFields) {
            setScreen(REQUEST_NEW_CARD);
        }
        if (screen === REQUEST_PAYMENT_METHOD && showAddFields) {
            setScreen(REQUEST_ADDITIONAL_FIELDS);
        }
        if (screen === REQUEST_NEW_CARD && showAddFields) {
            setScreen(REQUEST_ADDITIONAL_FIELDS);
        }
        if (screen === REQUEST_NEW_CARD && !showAddFields) {
            setScreen(REQUEST_PAYMENT_METHOD);
        }
        if (screen === REQUEST_ADDITIONAL_FIELDS) {
            setScreen(REQUEST_PAYMENT_METHOD);
        }
    };

    const screenValidation = (e) => {
        switch (screen) {
            case REQUEST_PAYMENT_METHOD:
            case REQUEST_NEW_CARD:
            case REQUEST_NEW_ACCOUNT:
            case REQUEST_ADDITIONAL_FIELDS: {
                return;
            }
            default: {
                e.preventDefault();
                switchToNextScreen();
            }
        }
    };

    const onClickHandler = (e) => {
        const isShowCvvPopup =
            screen === REQUEST_ADDITIONAL_FIELDS && fields && fields[PAYMENT_METHOD]?.value === PAYMENT_BANK_CARD && !fields[SOURCE_CARD_CVV]?.value;
        if (isShowCvvPopup) {
            e.preventDefault();
            const card = sourcesFrom[fields[SOURCE]?.value];
            showCvvPopup(card);
            switchToNextScreen();
        } else if (screen === REQUEST_NEW_CARD && hasFromFields(fields)) {
            e.preventDefault();
            switchToNextScreen(true);
        } else {
            screenValidation(e);
        }
    };

    const onBackIconClick = (e) => {
        if (screen === REQUEST_NEW_CARD) {
            e.preventDefault();
            setScreen(REQUEST_PAYMENT_METHOD);
        }
        if (screen === REQUEST_ADDITIONAL_FIELDS) {
            e.preventDefault();
            setScreen(REQUEST_PAYMENT_METHOD);
        }
    };
    if (paymentStatus.isShow && Object.keys(paymentStatus.status).length > 0) {
        return (
            <BaseLayout>
                {paymentStatus?.status?.status > STATUS_OK && (
                    <Failed status={paymentStatus.status} showCancelButton={true} toggleSpinner={props.toggleSpinner} />
                )}
                {paymentStatus?.status?.status === STATUS_OK && <Success status={paymentStatus.status} toggleSpinner={props.toggleSpinner} />}
                {paymentStatus?.status?.status < STATUS_OK && <Waiting status={paymentStatus.status} toggleSpinner={props.toggleSpinner} />}
            </BaseLayout>
        );
    } else {
        return (
            <>
                <CommonPage
                    backUrl={screen === REQUEST_PAYMENT_METHOD ? AUTH_REQUEST_MAIN_LINK : '#'}
                    onClickIcon={onBackIconClick}
                    leftCloseIcon={screen === REQUEST_PAYMENT_METHOD}
                    isTagA
                >
                    <Form
                        action={AUTH_REQUEST_PAY_PRECHECK}
                        getCanonicalPath={true}
                        onSubmitHandler={submitHandler}
                        checkNotVisibleField={false}
                        formName={REQUEST_FORM_NAME}
                    >
                        {screen === REQUEST_PAYMENT_METHOD && (
                            <>
                                {hasPaymentMethodsList && hasPaymentMethodsOrder ? (
                                    <PaymentMethodScreenSorted
                                        submitRef={submitRef}
                                        popupRef={popupRef}
                                        sourcesCards={sourcesFrom}
                                        sourcesBonus={sendFromBonusResource}
                                        sourcesGA={sendFromGAResources}
                                        amountValue={fields[AMOUNT_FROM]?.value}
                                        sourceCode={fields[SOURCE_CODE_FROM]?.value}
                                        formName={REQUEST_FORM_NAME}
                                        actionKeepAmount={actionKeepAmount}
                                        addCard={addNewCard}
                                        selectedCard={selectedCard}
                                        selectedBonus={selectedBonus}
                                        selectedGAccount={selectedGAccount}
                                        selectedGooglePay={selectedGooglePay}
                                    />
                                ) : (
                                    <PaymentMethodScreen
                                        submitRef={submitRef}
                                        sourcesCards={sourcesFrom}
                                        sourcesBonus={sendFromBonusResource}
                                        sourcesGA={sendFromGAResources}
                                        amountValue={fields[AMOUNT_FROM]?.value}
                                        sourceCode={fields[SOURCE_CODE_FROM]?.value}
                                        formName={REQUEST_FORM_NAME}
                                        actionKeepAmount={actionKeepAmount}
                                        addCard={addNewCard}
                                        selectedCard={selectedCard}
                                        selectedBonus={selectedBonus}
                                        selectedGAccount={selectedGAccount}
                                    />
                                )}
                            </>
                        )}
                        {screen === REQUEST_NEW_CARD && <NewCardScreen formName={REQUEST_FORM_NAME} />}
                        {screen === REQUEST_ADDITIONAL_FIELDS && <PayFromFields formName={REQUEST_FORM_NAME} />}
                        <FixedBottomPanel isRightAligned>
                            <Button
                                type="submit"
                                ref={submitRef}
                                raised={true}
                                caption={requestContinueButtonTitle}
                                variant={screen === REQUEST_PAYMENT_METHOD ? 'hidden' : null}
                                disabled={disabledSubmitButton}
                                onClick={onClickHandler}
                            />
                        </FixedBottomPanel>
                    </Form>
                </CommonPage>
                <PopupNew getPopupComponent={(component) => component} ref={popupRef} />
                <ModalNew ref={modalRef} getComponent={(component) => component} />
            </>
        );
    }
};

export default connect(null, {
    setNewFields,
    changeFormField,
    updateFormProperties,
    changeFormFieldValue,
    toggleModalWithTimer,
    toggleSpinner,
    updateFields,
})(BasePage(RequestPayPage));
